/**
 * EP 常挂 el-table--scrollable-x，即使列全可见（scrollWidth === clientWidth）。
 * 用实测横向溢出驱动缘态：pm-table-doc-pinned--x-overflow ⇔ 需要投影；否则显示 20px 短线。
 */

const EPS = 2;

export const PM_TABLE_DOC_PINNED_X_OVERFLOW = 'pm-table-doc-pinned--x-overflow';

function getScrollWrap(tableRoot) {
  if (!tableRoot) return null;
  return (
    tableRoot.querySelector('.el-scrollbar__wrap') ||
    tableRoot.querySelector('.el-table__body-wrapper')
  );
}

/** 以表体/表头 table 实际列宽与滚动视口对比，避免仅看 wrap.scrollWidth 与布局不同步 */
function measureDocPinnedXOverflow(tableRoot) {
  const wrap = getScrollWrap(tableRoot);
  if (!wrap) return false;

  const inner =
    wrap.querySelector('table') ||
    tableRoot.querySelector('.el-table__header-wrapper table');
  const contentWidth = inner
    ? Math.max(inner.offsetWidth, inner.scrollWidth)
    : wrap.scrollWidth;
  const viewportWidth = wrap.clientWidth;
  return contentWidth - viewportWidth > EPS;
}

function observeOverflowHosts(tableRoot, wrap, resizeObserver) {
  let node = wrap;
  let depth = 0;
  while (node && depth < 6) {
    resizeObserver?.observe(node);
    node = node.parentElement;
    depth += 1;
  }
  const preview = tableRoot.closest('.component-preview, .pm-el-table-tree-demo-shell, .steel-opt-concrete-tree-table__viewport');
  if (preview) resizeObserver?.observe(preview);
}

export function updatePmElTableDocPinnedOverflowClass(tableRoot) {
  if (!tableRoot?.classList?.contains('pm-el-table--doc-pinned')) return;
  tableRoot.classList.toggle(PM_TABLE_DOC_PINNED_X_OVERFLOW, measureDocPinnedXOverflow(tableRoot));
}

/**
 * @param {HTMLElement | null | undefined} tableRoot `.el-table.pm-el-table--doc-pinned`
 * @returns {() => void} cleanup
 */
export function bindPmElTableDocPinnedOverflowClass(tableRoot) {
  if (!tableRoot?.classList?.contains('pm-el-table--doc-pinned')) {
    return () => {};
  }

  tableRoot.__pmDocPinnedOverflowCleanup?.();

  const wrap = getScrollWrap(tableRoot);
  if (!wrap) return () => {};

  const update = () => {
    tableRoot.__vueParentComponent?.proxy?.doLayout?.();
    updatePmElTableDocPinnedOverflowClass(tableRoot);
  };
  update();
  requestAnimationFrame(() => requestAnimationFrame(update));

  wrap.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });

  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
  ro?.observe(wrap);
  ro?.observe(tableRoot);
  observeOverflowHosts(tableRoot, wrap, ro);

  const mo =
    typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => requestAnimationFrame(update))
      : null;
  mo?.observe(tableRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

  const cleanup = () => {
    wrap.removeEventListener('scroll', update);
    window.removeEventListener('resize', update);
    ro?.disconnect();
    mo?.disconnect();
    tableRoot.classList.remove(PM_TABLE_DOC_PINNED_X_OVERFLOW);
    if (tableRoot.__pmDocPinnedOverflowCleanup === cleanup) {
      tableRoot.__pmDocPinnedOverflowCleanup = null;
    }
  };

  tableRoot.__pmDocPinnedOverflowCleanup = cleanup;
  return cleanup;
}

/** @deprecated 使用 bindPmElTableDocPinnedOverflowClass */
export const bindPmElTableScrollEdgeClasses = bindPmElTableDocPinnedOverflowClass;

/** @deprecated 使用 updatePmElTableDocPinnedOverflowClass */
export const updatePmElTableScrollEdgeClasses = updatePmElTableDocPinnedOverflowClass;
